import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { h6 } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Button/Button.styles';
import { m } from 'framer-motion';
import styled, { createGlobalStyle, css } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --floatingCtaHeight: 77px;
  }
`;
const ControlButton = styled.button `
  ${ButtonReset};

  position: absolute;
  right: 14px;
  top: 24px;
  width: 20px;

  svg {
    ${transition('color')}

    display: block;
    transform: translateY(8px) rotate(0deg);
    width: 100%;
    color: ${brand.white};

    &[data-icon='close'] {
      display: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    svg {
      &[data-icon='close'] {
        display: block;
      }

      &[data-icon='chevron'] {
        display: none;
      }
    }
  }
`;
const baseWrapperStyles = css `
  --paddingX: ${fluid(8, 16, Device.MobileLarge, Device.Tablet)};

  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px var(--paddingX);

  ${ButtonStyles.Container} {
    justify-content: center;
    text-align: center;
    width: 100%;
    max-width: 400px;
    border-radius: 12px;
    text-wrap: balance;

    &[data-type='inverted'] {
      --colorFg: #888888;
    }

    @media ${until(Device.TabletLarge)} {
      padding-top: 8px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    --paddingX: 48px;

    flex-direction: column;
    gap: 0px;
  }
`;
const MainCtaWrapper = styled.div `
  ${baseWrapperStyles};
  color: ${brand.black};
  background-color: ${brand.beige};

  @media ${from(Device.TabletLarge)} {
    ${ButtonStyles.Container}[data-type='inverted'] {
      display: none;
    }
  }
`;
const descriptionVariants = {
    hidden: {
        opacity: 0,
        transition: {
            duration: 0.15,
        },
    },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.35,
        },
    },
};
const Description = styled(m.p).attrs({ variants: descriptionVariants }) `
  text-align: center;
  ${h6};
  margin: 0px 0px 16px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const wrapperVariants = {
    hidden: {
        height: 0,
        transition: {
            duration: 0.3,
        },
    },
    visible: {
        height: 'auto',
        transition: {
            duration: 0.35,
            staggerChildren: 0.1,
        },
    },
};
const AnimatedWrapper = styled(m.div).attrs({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: wrapperVariants,
}) `
  overflow: hidden;

  @media ${until(Device.TabletLarge)} {
    &[data-secondary] {
      display: none !important;
    }
  }
`;
const SecondaryCtaWrapper = styled(m.div) `
  ${baseWrapperStyles};
  color: ${brand.white};
  background-color: ${brand.green};
`;
const containerVariants = {
    hidden: {
        '--containerY': '100%',
        transition: { duration: 0.4 },
    },
    visible: {
        '--containerY': '0%',
        transition: { duration: 0.4, staggerChildren: 0.1 },
    },
};
const Container = styled(m.div).attrs({
    initial: 'hidden',
    animate: 'visible',
    variants: containerVariants,
}) `
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: ${brand.beige};
  z-index: ${Layers.HeaderBackdrop};
  width: 100%;

  display: flex;
  flex-direction: column;
  box-shadow: 0px -4px 6px rgba(0 0 0 / 10%);

  transform: translateY(var(--containerY));

  @media ${from(Device.TabletLarge)} {
    cursor: pointer;

    ${({ isOpen }) => isOpen
    ? null
    : css `
            transform: translateY(100%);
            transition: transform 0.4s ease-out;
            pointer-events: none;
          `}
  }
`;
const FloatingCtaStyles = {
    Container,
    ControlButton,
    MainCtaWrapper,
    Description,
    AnimatedWrapper,
    SecondaryCtaWrapper,
    GlobalStyles,
};
export default FloatingCtaStyles;
